import { grey } from "@mui/material/colors";
import { TCardMaster } from "models/CardMasterModel";
import { TCard } from "models/CardModel";
import { getCardProps, TCardProps } from "./card";

const tableSx = {
  borderCollapse: "separate",
  borderBottom: "1px solid",
  borderRight: "1px solid",
  borderColor: grey[400],
  tbody: {
    tr: {
      // tbody の tr が偶数行の場合は、td の背景色を変える
      "&:nth-of-type(even)": {
        td: {
          backgroundColor: grey[100],
        },
      },
      // tbody の tr が hover したら、td の背景色を変える
      "&:hover": {
        td: {
          backgroundColor: grey[200],
        },
      },
    },
  },
};

const tdSx = {
  padding: 0.5,
  whiteSpace: "nowrap",
  border: 1,
  borderColor: grey[400],
  backgroundColor: "white",
  borderWidth: "1px 0 0 1px",
};

const numFormatter = new Intl.NumberFormat("ja-JP");
const priceScaleSigns = {
  1: undefined,
  1000: "千円",
  1000000: "百万円",
};

const doNotScale = (fieldConfig: any, priceScale?: TCard["price_scale"]) => {
  if (!priceScale || fieldConfig.format !== "number_format" || priceScale <= 1) return true;
  return fieldConfig.value_type !== "price";
};

const addScale2Name = (name: any, fieldConfig: any, priceScale?: TCard["price_scale"]) => {
  if (doNotScale(fieldConfig, priceScale)) return name;
  return priceScale ? `${name}(${priceScaleSigns[priceScale]})` : name;
};

const Triangles: { [key: string]: string } = { black: "▲", white: "△" };

const getMinusSetting = (value: any, fieldConfig: any) => {
  return Number(value) < 0
    ? {
        style: { color: fieldConfig.minus_color === "normal" ? undefined : "red" },
        suffix: fieldConfig.minus_icon ? Triangles[fieldConfig.minus_icon] : "-",
      }
    : {};
};

const cellValueFormatter = (value: any, fieldConfigOrg: any, subField: string, priceScale?: TCard["price_scale"]) => {
  const fieldConfig = { ...fieldConfigOrg };
  if (subField.indexOf("_PERCENT") > 0) {
    fieldConfig.format = "percentage";
    fieldConfig.value_type = "percentage";
  }
  const format = fieldConfig["format"];
  if (!value || !format) return String(value);
  let newValue = value;
  let result = "";
  const minusSettings = getMinusSetting(newValue, fieldConfig);
  switch (format) {
    case "number_format":
      if (priceScale && priceScale > 1 && !doNotScale(fieldConfig, priceScale)) newValue = value / priceScale;
      result = numFormatter.format(Number(Math.round(newValue)));
      break;
    case "percentage":
      result = Number(newValue).toFixed(1);
      break;
    case "float_hour_to_hhmm":
      // newValue には、8.5 のような数値が入っている。これを、 "08:30" に変換する。
      result = `${String(Math.floor(newValue)).padStart(2, "0")}:${String(Math.round((newValue % 1) * 60)).padStart(2, "0")}`;
      break;
    default:
      result = newValue;
  }
  return (
    <span style={minusSettings.style}>
      {minusSettings.suffix}
      {String(result).replace("-", "")}
    </span>
  );
};

type TIsOutOfDate = (ym: string, card: TCard) => boolean;
const isOutOfDate: TIsOutOfDate = (dateKey, card) => {
  const ym = dateKey.slice(0, 4) + dateKey.slice(4, 6);
  const { monthProps } = getCardProps(card);

  // もし、ym に数値以外の文字列が入っていたら、isOutOfDateHQ を return。そうでない場合は isOutOfDateMonth を return
  return isNaN(Number(ym)) ? isOutOfDateHQ(ym, monthProps) : isOutOfDateMonth(ym, monthProps);
};
const isOutOfDateMonth = (ym: string, monthProps: TCardProps["monthProps"]) => {
  if (
    (monthProps.fromYM && Number(ym) < Number(monthProps.fromYM)) ||
    (monthProps.toYM && Number(ym) > Number(monthProps.toYM))
  ) {
    return true;
  }
  const m = ym.slice(-2);
  if (monthProps.includeM.length > 0 && !monthProps.includeM.includes(Number(m))) {
    return true;
  }
  return false;
};
const isOutOfDateHQ = (ym: string, monthProps: TCardProps["monthProps"]) => {
  const m = ym.slice(-2);
  if (monthProps.includeHQ.length > 0 && !monthProps.includeHQ.includes(m)) {
    return true;
  }
  return false;
};

type TFieldFilter = (
  type: "kamoku" | "subfield" | "totalValueKeys",
  key: any,
  card: TCard,
  cardMaster: TCardMaster,
) => boolean;
const fieldFilter: TFieldFilter = (type, key, card, cardMaster) => {
  const { kamokuProps, subfieldProps, totalValueKeysProps } = getCardProps(card, cardMaster);
  const props =
    type === "kamoku"
      ? kamokuProps
      : type === "subfield"
        ? subfieldProps
        : type === "totalValueKeys"
          ? totalValueKeysProps
          : null;
  if (!props || props.list.length === 0) return true;
  const bool = props.list.includes(String(key));
  return props.mode === "show" ? bool : !bool;
};

type TFieldSort = (a: any, b: any, kamokuProps: any) => number;
const fieldSort: TFieldSort = (a, b, kamokuProps) => {
  if (kamokuProps.sort.length === 0) return 1;
  if (a["aggregate"] && b["aggregate"]) {
    if (a["aggregate"] > b["aggregate"]) return 1;
    if (a["aggregate"] < b["aggregate"]) return -1;
  }
  return kamokuProps.sort.indexOf(String(a["target"])) > kamokuProps.sort.indexOf(String(b["target"])) ? 1 : -1;
};

type TTableSort = (a: any, b: any, card: TCard, cardMaster: TCardMaster) => number;
const tableSort: TTableSort = (a, b, card, cardMaster) => {
  if (!card.thSort) return 0;
  const { keys: sortKey, order: sortOrder } = card.thSort;
  const valuesType = card.thSort.valuesType || "values";
  if (!sortKey) return 0;

  const getValue = (ab: any) => {
    if (valuesType === "total_values") {
      const [totalKey] = sortKey;
      return ab.total_values[totalKey];
    } else if (card.vh_mode === "vt" || cardMaster.isDaily) {
      const [ym, subfield] = sortKey;
      return ab.dates[ym][valuesType][subfield];
    } else {
      const [ym, kamoku, subfield] = sortKey;
      return ab.dates[ym][kamoku][valuesType][subfield];
    }
  };

  const valueA = Number(getValue(a));
  const valueB = Number(getValue(b));
  if (valueA === valueB) return 0;
  if (sortOrder === "asc") return valueA > valueB ? 1 : -1;
  else return valueA < valueB ? 1 : -1;
};

type TIsSortable = (card: TCard) => boolean;
const isSortable: TIsSortable = (card) => {
  if (card.vh_mode === "hn") return true;
  return card.kamoku_list?.length === 1 && card.kamoku_list_mode === "show";
};

const makeColsSticky = (table: HTMLTableElement) => {
  const rows = table.getElementsByTagName("tr");
  Object.values(rows).forEach((row) => {
    let left = 0;
    let last: any = null;
    Object.values(row.getElementsByClassName("stickyCell")).forEach((c) => {
      const cell = c as HTMLElement;
      const rect = cell.getBoundingClientRect();
      cell.style.position = "sticky";
      cell.style.zIndex = "1";
      cell.style.left = left + "px";
      cell.style.width = rect.width + "px";
      left += rect.width;
      last = cell;
    });
    if (last) last.style.borderRight = "1px solid #bdbdbd";
  });
};

export const TableUtil = {
  tableSx,
  tdSx,
  addScale2Name,
  cellValueFormatter,
  isOutOfDate,
  fieldFilter,
  fieldSort,
  tableSort,
  isSortable,
  makeColsSticky,
};
